<template>
  <div class="row">
    <div class="col-12">
      <p class="lead">AR - Exportação para PDF</p>

      <machine-filter @filter="onFilter"></machine-filter>

      <DataTable
        :value="machines"
        :lazy="true"
        :paginator="false"
        :paginator-position="'both'"
        :rows-per-page-options="[200]"
        :rows="200"
        :total-records="totalRecords"
        :loading="loading"
        :row-hover="true"
        @page="onPage($event)"
        @sort="onSort($event)"
        ref="dt"
        data-key="id"
        class="p-datatable-gridlines p-datatable-sm"
        id="print_me"
      >
        <template #header>
          <div>
            <Button label="Exportar PDF" class="p-button-secondary" title="Exportar PDF" icon="pi pi-file-pdf" icon-position="right" @click="exportToPDF"/>
          </div>
        </template>
        <template #empty> Nenhum item encontrado </template>
        <template #loading> Carregando </template>
        <Column header="Foto">
          <template #body="{ data }">
            <img
              class="table-image expandable-image"
              :src="data.picture_url"
              alt=""
              @click="showSingleImage(data.picture_url)"
            />
          </template>
        </Column>
        <Column header="Identificação">
          <template #body="{ data }">
            <div>
              <b>Classe Equip.:</b> {{ data.machine_class_name || '-' }}
            </div>
            <div><b>Tag Equip.:</b> {{ data.machine_tag }}</div>
            <div><b>Equip. Primário:</b> {{ data.primary_machine_tag }}</div>
            <div><b>Tag Ambra:</b> {{ data.ambra_tag }}</div>
            <div><b>ID:</b> {{ data.id }}</div>
          </template>
        </Column>
        <Column header="Localização">
          <template #body="{ data }">
            <div><b>Área:</b> {{ data.company_area_name }}</div>
            <div><b>Setor:</b> {{ data.company_sector_name }}</div>
            <div><b>Complemento:</b> {{ data.company_complement_name }}</div>
          </template>
        </Column>
        <Column header="Perigos">
          <template #body="{ data }">
            {{ data.natures_of_risk }}
          </template>
        </Column>
        <Column header="Galeria">
          <template #body="{ data }">
            <div>
              <analysis-images-gallery
                :images="getAnalysisImages(data)"
                @open-gallery="handleOpenGallery"
              ></analysis-images-gallery>
            </div>
          </template>
        </Column>
        <Column header="Não Conformidades">
          <template #body="{ data }">
            <table class="mb-2 inner-table hrn-level-table">
              <tbody>
                <tr>
                  <td
                    :style="{ fontWeight: data.hrn_level_acceptable && 'bold' }"
                  >
                    {{ getLevelCountValue(data.hrn_level_acceptable) }}
                  </td>
                  <td
                    :style="{
                      backgroundColor:
                        data.hrn_level_acceptable && getHrnLevelColor(1),
                    }"
                  >
                    Aceitável
                  </td>
                </tr>
                <tr>
                  <td
                    :style="{ fontWeight: data.hrn_level_very_low && 'bold' }"
                  >
                    {{ getLevelCountValue(data.hrn_level_very_low) }}
                  </td>
                  <td
                    :style="{
                      backgroundColor:
                        data.hrn_level_very_low && getHrnLevelColor(2),
                    }"
                  >
                    Muito Baixo
                  </td>
                </tr>
                <tr>
                  <td :style="{ fontWeight: data.hrn_level_low && 'bold' }">
                    {{ getLevelCountValue(data.hrn_level_low) }}
                  </td>
                  <td
                    :style="{
                      backgroundColor:
                        data.hrn_level_low && getHrnLevelColor(3),
                    }"
                  >
                    Baixo
                  </td>
                </tr>
                <tr>
                  <td
                    :style="{
                      fontWeight: data.hrn_level_significant && 'bold',
                    }"
                  >
                    {{ getLevelCountValue(data.hrn_level_significant) }}
                  </td>
                  <td
                    :style="{
                      backgroundColor:
                        data.hrn_level_significant && getHrnLevelColor(4),
                    }"
                  >
                    Significante
                  </td>
                </tr>
                <tr>
                  <td :style="{ fontWeight: data.hrn_level_high && 'bold' }">
                    {{ getLevelCountValue(data.hrn_level_high) }}
                  </td>
                  <td
                    :style="{
                      backgroundColor:
                        data.hrn_level_high && getHrnLevelColor(5),
                    }"
                  >
                    Alto
                  </td>
                </tr>
                <tr>
                  <td
                    :style="{ fontWeight: data.hrn_level_very_high && 'bold' }"
                  >
                    {{ getLevelCountValue(data.hrn_level_very_high) }}
                  </td>
                  <td
                    :style="{
                      backgroundColor:
                        data.hrn_level_very_high && getHrnLevelColor(6),
                    }"
                  >
                    Muito Alto
                  </td>
                </tr>
                <tr>
                  <td :style="{ fontWeight: data.hrn_level_extreme && 'bold' }">
                    {{ getLevelCountValue(data.hrn_level_extreme) }}
                  </td>
                  <td
                    :style="{
                      backgroundColor:
                        data.hrn_level_extreme && getHrnLevelColor(7),
                    }"
                  >
                    Extremo
                  </td>
                </tr>
                <tr>
                  <td
                    :style="{
                      fontWeight: data.hrn_level_unacceptable && 'bold',
                    }"
                  >
                    {{ getLevelCountValue(data.hrn_level_unacceptable) }}
                  </td>
                  <td
                    :class="{ unacceptable: !!data.hrn_level_unacceptable }"
                    :style="{
                      backgroundColor:
                        data.hrn_level_unacceptable && getHrnLevelColor(8),
                    }"
                  >
                    Inaceitável
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="total">
              <b>Total: </b> <span>{{ data.noncompliances_total || '-' }}</span>
            </div>
          </template>
        </Column>
        <Column header="Risco Geral Equipamento">
          <template #body="{ data }">
            <table class="mb-2 inner-table" style="">
              <thead>
                <tr>
                  <th>PO</th>
                  <th>GPD</th>
                  <th>FE</th>
                  <th>NP</th>
                  <th>RES</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ data.po || '-' }}</td>
                  <td>{{ data.gpd || '-' }}</td>
                  <td>{{ data.fe || '-' }}</td>
                  <td>{{ data.np || '-' }}</td>
                  <td
                    :class="{ unacceptable: data.hrn_level === 8 }"
                    :style="`background-color: ${getHrnLevelColor(
                      data.hrn_level,
                    )}`"
                    width="60px"
                  >
                    {{ formatHrnResult(data.hrn_result) || '-' }}
                  </td>
                </tr>
              </tbody>
            </table>

            <table-iso-138491 :machine-id="data.id"></table-iso-138491>

            <table class="inner-table" v-if="isCompanyMondelez()">
              <thead>
                <tr>
                  <th colspan="4">Mondelez</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Sev.</td>
                  <td>{{ data.mondelez_severity || '-' }}</td>
                </tr>
                <tr>
                  <td>Prob.</td>
                  <td>{{ data.mondelez_probability || '-' }}</td>
                </tr>
                <tr>
                  <td>Res.</td>
                  <td
                    :style="{
                      backgroundColor:
                        data.mondelez_result &&
                        getMondelezLevelColor(data.mondelez_result),
                    }"
                  >
                    {{ data.mondelez_result || '-' }} ({{
                      data.mondelez_result_label || '-'
                    }})
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </Column>
        <Column header="Ações">
          <template #body="{ data }">
            <div>
              <router-link
                :to="{ name: 'risk-analyses', params: { machineId: data.id } }"
                target="_blank"
                class=""
              >
                Ver AR
              </router-link>
            </div>
            <div>
              <router-link
                :to="{
                  name: 'action-plan-form',
                  params: { filters: JSON.stringify({ machine_id: data.id }) },
                }"
                class=""
              >
                Plano de ação
              </router-link>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
  import { computed, defineAsyncComponent } from 'vue'
  import { useStore } from 'vuex'

  import DataTable from 'primevue/datatable'
  import Column from 'primevue/column'
  import Button from 'primevue/button';

  import AnalysisImagesGallery from '../components/AnalysisImagesGallery'

  import { MONDELEZ_ID } from '@/constants/config.constants.js'

  import useHrn from '@/modules/Hrn.js'
  import useMondelez from '@/modules/Mondelez.js'
  import useImageViewer from '@/modules/ImageViewer.js'
  import { useRouter } from 'vue-router'
  import jsPDF from 'jspdf'
  import * as html2canvas from 'html2canvas'

  const MachineFilter = defineAsyncComponent({
    loader: () => import('@/components/common/Filter/Filter.vue'),
  })
  const TableIso138491 = defineAsyncComponent({
    loader: () =>
      import('@/components/risk-analyses/components/TableIso138491.vue'),
  })

  export default {
    setup() {
      const store = useStore()
      const router = useRouter()
      const selected_company = computed(() => store.getters.selected_company)
      const selected_order_of_service = computed(() => store.getters.selected_order_of_service)
      const { getHrnLevelColor } = useHrn()
      const { getMondelezLevelColor } = useMondelez()
      const { showImageIndex, showSingleImage } = useImageViewer()
      const perPage = 200

      return {
        selected_company,
        selected_order_of_service,
        getHrnLevelColor,
        getMondelezLevelColor,
        showImageIndex,
        showSingleImage,
        router,
      }
    },
    components: {
      DataTable,
      Column,
      MachineFilter,
      AnalysisImagesGallery,
      TableIso138491,
      Button,
    },
    data() {
      return {
        machines: [],
        totalRecords: 0,
        lazyParams: {},
        filters: {},
        loading: true,
        registros: [],
        company_name: null,
        printLoading: true,
      }
    },
    mounted() {
      this.lazyParams = {
        first: 0,
        rows: this.$refs.dt.rows,
        sortField: null,
        sortOrder: null,
        filters: this.filters,
        registros: [],
      }
      this.loadLazyData()

  },
    methods: {

      exportToPDF () {

        // let windowHeight = window.innerHeight;
        // let windowWidth = window.innerWidth;
        //
        // let pdf = new jsPDF('l,','mm','a4');
        //
        // let canvasElement = document.createElement("canvas");
        // canvasElement.width = windowWidth;
        // canvasElement.height = windowHeight;
        //
        // html2canvas(this.$refs.$dt, {
        //     canvas: canvasElement,
        //     width: windowWidth,
        //     height: windowHeight
        // }).then(canvas => {
        //     const img = canvas.toDataURL("image/jpeg", 1);
        //     document.body.appendChild(canvas);
        //     pdf.addImage(img, "PNG", 10, 10);
        //     pdf.save("ar.pdf");
        //
        //     alert('works')
        // }).catch(err => {
        //     alert('error')
        // });
      },
      loadLazyData(page = 1) {
        var app = this

        this.loading = true

        const queryString = this.getQueryString(page, this.lazyParams.rows)

        if (this.selected_order_of_service.id) {
          app.axios
            .get(
              `/api/machines/all?order_of_service_id=${this.selected_order_of_service.id}${queryString}`,
            )
            .then(({ data }) => {
              app.machines = data.data
              this.totalRecords = data.total
              this.registros = app.machines
            })
            .catch((error) => {
              console.log(error)
            })
            .finally(() => {
              app.loading = false
            })
        }
      },
      getQueryString(page, perPage) {
        let query = ''

        if (page) query += `&page=${page}`

        if (perPage) query += `&perPage=${perPage}`

        if (this.filters) {
          const queryStringFilters = Object.keys(this.filters)
            .filter((key) => this.filters[key])
            .map((key) => key + '=' + this.filters[key])
            .join('&')

          query += `&${queryStringFilters}`
        }

        return query
      },
      formatHrnResult(hrn) {
        const hrnFormatted = parseFloat(hrn).toFixed(3)
        if (isNaN(hrnFormatted)) return ''
        return hrnFormatted
      },
      getLevelCountValue(levelCount) {
        return !isNaN(levelCount) ? levelCount : '-'
      },
      getAnalysisImages(data) {
        const images = data?.analysis_pictures?.split(',')

        if (!images) return []
        this.imagens = images;

        return images
      },
      isCompanyMondelez() {
        return this.selected_company.id === MONDELEZ_ID
      },
      onPage(event) {
        this.lazyParams = event

        const { page } = event

        this.loadLazyData(page + 1)
      },
      onFilter($event) {
        this.filters = $event

        this.loadLazyData()
      },
      handleOpenGallery(event) {
        const { images, index } = event

        this.showImageIndex(images, index)
      },
    },
    watch: {
      selected_order_of_service() {
        this.loadLazyData()
      },
    },
  }
</script>

<style lang="scss" scoped>
  .table-image {
    max-width: 80px;
    max-height: 100px;
    object-fit: cover;
  }

  .unacceptable {
    color: #fff;
  }

  .inner-table,
  td,
  td {
    border: 1px solid #ccc;
    border-collapse: collapse;
  }

  .inner-table thead {
    background-color: #eaeaea;
  }

  .hrn-level-table {
    min-width: 120px;
    width: auto !important;

    td {
      padding: 5px;
    }
  }

  .expandable-image {
    cursor: pointer;
  }

  .printing {
  font-family: sans-serif;
  max-width: 600px;
  border: solid 1px #ccc;
  text-align: center;
  padding: 1em;
  margin: 2em auto;
  }
</style>
